// src/lib/taskPriority.ts
import type { PriorityQuadrant } from '../types/task.type';

/** 1~5 범위 강제 */
function clamp11to55(n: any): 1 | 2 | 3 | 4 | 5 {
    const v = Number(n);
    const c = Math.max(1, Math.min(5, Math.round(v)));
    return c as 1 | 2 | 3 | 4 | 5;
}

/** urgency × importance (1~25) */
export function calcPriorityScore(urgency?: number | null, importance?: number | null): number | null {
    if (!Number.isFinite(urgency as number) || !Number.isFinite(importance as number)) return null;
    const u = clamp11to55(urgency);
    const i = clamp11to55(importance);
    return u * i;
}

/** Eisenhower Matrix 분류 */
export function quadrantFromUI(urgency?: number | null, importance?: number | null): PriorityQuadrant | null {
    if (!Number.isFinite(urgency as number) || !Number.isFinite(importance as number)) return null;
    const u = clamp11to55(urgency);
    const i = clamp11to55(importance);

    const isHighI = i >= 3;
    const isHighU = u >= 3;

    if (isHighI && isHighU) return 'DO_NOW'; // 중요+긴급
    if (isHighI && !isHighU) return 'SCHEDULE'; // 중요+비긴급
    if (!isHighI && isHighU) return 'DELEGATE'; // 비중요+긴급
    return 'ELIMINATE'; // 비중요+비긴급
}

/** 점수 기반 배경/문자 컬러 */
export function priorityPillClass(score?: number | null): string {
    const s = Number.isFinite(score as number) ? (score as number) : 0;
    if (s <= 0) return 'bg-gray-100 text-gray-700';
    if (s >= 16) return 'bg-rose-100 text-rose-700';
    if (s >= 9) return 'bg-amber-100 text-amber-700';
    return 'bg-emerald-100 text-emerald-700';
}

/** 사분면 표시 문자열 */
export function quadrantLabel(q?: PriorityQuadrant | null): string {
    switch (q) {
        case 'DO_NOW':
            return 'DO NOW';
        case 'SCHEDULE':
            return 'SCHEDULE';
        case 'DELEGATE':
            return 'DELEGATE';
        case 'ELIMINATE':
            return 'ELIMINATE';
        default:
            return '';
    }
}

/**
 * 저장값 우선 사용 + 없으면 보완 계산
 * - UI에서 "10 DELEGATE" 같은 텍스트와 pill 클래스 한번에 얻기
 */
export function formatPriority(params: {
    scoreStored?: number | null;
    quadStored?: PriorityQuadrant | null;
    urgency?: number | null;
    importance?: number | null;
}) {
    const { scoreStored, quadStored, urgency, importance } = params;

    const score = Number.isFinite(scoreStored as number)
        ? (scoreStored as number)
        : calcPriorityScore(urgency, importance) ?? 0;

    const quadrant = quadStored ?? quadrantFromUI(urgency, importance);

    const className = priorityPillClass(score);
    const text = quadrant ? `${score} ${quadrantLabel(quadrant)}` : String(score);

    return { score, quadrant, text, className };
}
